/* eslint-disable react-hooks/exhaustive-deps */
/**
 * MyFitProfile — customer's saved body scans dashboard.
 *
 * Shows the ACTIVE profile prominently (measurements + avatar reference +
 * scan date + provider badge), plus a compact history timeline of previous
 * scans with one-click "rollback" to any older scan.
 *
 * Rescan button opens the 3DLOOK Mobile Tailor widget. In mock mode this is
 * a placeholder URL served by our own backend; in live mode it's a real SAIA
 * widget URL. The flow is identical either way.
 */
import ProviderMeasurements from './ProviderMeasurements';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, RefreshCw, ArrowLeft, Loader2, Check, History,
  ChevronDown, ChevronUp, Ruler, ShieldCheck, ExternalLink, Trash2,
} from 'lucide-react';
import { bodyProfileAPI, scanAPI } from '../api';
import StartBodyScanButton from './StartBodyScanButton';

const GOLD  = '#D4AF37';
const INK   = '#0F0F0F';
const MUTED = '#8a8a8a';
const LINE  = '#E5E5E5';
const SOFT  = '#FAF9F6';
const GREEN = '#166534';

// Summary cards: the provider fields behind the app's chest/waist/hip/shoulder/sleeve/inseam/neck.
const PRIMARY_PROVIDER_KEYS = [
  'volume_params.chest', 'volume_params.waist', 'volume_params.low_hips', 'front_params.shoulders',
  'front_params.sleeve_length', 'front_params.inseam', 'volume_params.neck',
];
const humanize = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};
const daysSince = (iso) => {
  if (!iso) return null;
  try { return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24)); }
  catch { return null; }
};

const MeasurementCard = ({ label, value, unit = 'cm' }) => (
  <div className="p-3 bg-white" style={{ border: `1px solid ${LINE}` }}
    data-testid={`fit-measure-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="text-[9px] tracking-[0.14em] uppercase" style={{ color: MUTED, fontWeight: 600 }}>
      {label}
    </p>
    <p className="mt-1" style={{ fontFamily: 'Playfair Display, serif', color: INK, fontSize: 22, fontWeight: 500 }}>
      {value != null ? `${value}` : '—'}
      <span className="text-[10px] ml-1" style={{ color: MUTED }}>{unit}</span>
    </p>
  </div>
);

const HistoryRow = ({ scan, onActivate, onDelete, isActive }) => {
  const [busy, setBusy] = useState(false);
  return (
    <div className="p-3 bg-white flex items-center justify-between gap-3"
      style={{ border: `1px solid ${isActive ? GOLD : LINE}` }}
      data-testid={`history-row-${scan.profile_id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-[11.5px]" style={{ color: INK, fontWeight: 500 }}>
            {fmtDate(scan.scan_date)}
          </p>
          <span className="text-[8.5px] tracking-[0.10em] uppercase px-1.5 py-0.5"
            style={{
              background: isActive ? GOLD : 'rgba(0,0,0,0.06)',
              color: isActive ? '#FFF' : MUTED,
              fontWeight: 700,
            }}>
            {isActive ? 'Active' : (scan.provider || 'archived')}
          </span>
        </div>
        <p className="text-[10px] mt-0.5" style={{ color: MUTED }}>
          H {scan.height_cm}cm · W {scan.weight_kg}kg
          {scan.confidence?.overall && <> · {scan.confidence.overall}% AI</>}
        </p>
      </div>
      {!isActive && (
        <>
          <button
            data-testid={`activate-${scan.profile_id}`}
            disabled={busy}
            onClick={async () => {
              setBusy(true); try { await onActivate(scan.profile_id); } finally { setBusy(false); }
            }}
            className="text-[9.5px] tracking-[0.14em] uppercase px-2 py-1.5"
            style={{ border: `1px solid ${INK}`, color: INK, fontWeight: 600 }}>
            {busy ? '…' : 'Use This'}
          </button>
          <button
            data-testid={`delete-${scan.profile_id}`}
            disabled={busy}
            onClick={async () => {
              if (!window.confirm('Delete this scan?')) return;
              setBusy(true); try { await onDelete(scan.profile_id); } finally { setBusy(false); }
            }}
            className="p-1.5" style={{ color: MUTED }}>
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        </>
      )}
    </div>
  );
};

const MyFitProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [c, h] = await Promise.all([bodyProfileAPI.current(), bodyProfileAPI.history()]);
      setProfile(c.profile);
      setHistory(h.scans || []);
    } catch (e) {
      // Body Measurement requires login — send guests to Sign In and back here.
      if (e.status === 401) {
        navigate('/auth', { replace: true, state: { redirectTo: '/my-fit-profile' } });
        return;
      }
      setError(e.message || 'Could not load your fit profile');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleActivate = async (pid) => {
    await bodyProfileAPI.activate(pid);
    await load();
  };
  const handleDelete = async (pid) => {
    await bodyProfileAPI.remove(pid);
    await load();
  };
  const handleScanComplete = async () => {
    await load();
  };

  const measurements = profile?.measurements || {};
  const provider = profile?.provider_measurements;
  const hasProvider = provider?.count > 0;
  // Exact 3DLOOK values (full precision) when the provider dataset exists.
  const providerByKey = Object.fromEntries((provider?.items || []).map((it) => [it.key, it]));
  const primaryProvider = PRIMARY_PROVIDER_KEYS.map((key) => ({ key, item: providerByKey[key] }));
  // Legacy profiles without the provider dataset: rounded summary; the chest→bust copy is not shown as a bust.
  const isBustCopy = measurements.bust != null && measurements.bust === measurements.chest;
  const ORDER = ['bust', 'chest', 'waist', 'hip', 'shoulder', 'sleeve_length', 'inseam', 'neck'];
  const primary = ORDER.filter((k) => measurements[k] != null && !(k === 'bust' && isBustCopy));
  const rest    = Object.keys(measurements).filter((k) => !ORDER.includes(k) && measurements[k] != null);
  const age     = daysSince(profile?.scan_date);

  return (
    <div className="min-h-screen" style={{ background: SOFT }} data-testid="my-fit-profile-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b" style={{ borderColor: LINE }}>
        <div className="px-4 py-3 flex items-center gap-3 max-w-2xl mx-auto">
          <button onClick={() => navigate(-1)} data-testid="back-btn" className="p-1.5 -ml-1.5">
            <ArrowLeft className="w-5 h-5" style={{ color: INK }} strokeWidth={1.5} />
          </button>
          <div className="flex-1">
            <p className="text-[9px] tracking-[0.22em] uppercase" style={{ color: GOLD, fontWeight: 700 }}>
              Lunora AI Tape
            </p>
            <h1 style={{ fontFamily: 'Playfair Display, serif', color: INK, fontSize: 20, fontWeight: 500 }}>
              My Fit Profile
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5">
        {loading && (
          <div className="flex items-center gap-2 py-8 justify-center" data-testid="fit-loading">
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: GOLD }} strokeWidth={1.6} />
            <span className="text-[12px]" style={{ color: MUTED }}>Loading your profile…</span>
          </div>
        )}

        {error && !loading && (
          <div className="p-4 mb-4" style={{ background: '#FEF2F2', border: '1px solid #FCA5A5' }}>
            <p className="text-[12px]" style={{ color: '#991B1B' }}>{error}</p>
            <button onClick={load} className="mt-2 text-[10px] tracking-[0.12em] uppercase underline"
              style={{ color: '#991B1B' }}>Retry</button>
          </div>
        )}

        {!loading && !error && !profile && (
          <div className="p-5 mb-4 text-center" data-testid="fit-empty-state"
            style={{ background: '#FFF', border: `1px dashed ${GOLD}` }}>
            <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: GOLD }} strokeWidth={1.5} />
            <h2 style={{ fontFamily: 'Playfair Display, serif', color: INK, fontSize: 20, fontWeight: 500 }}>
              Your fit profile is empty
            </h2>
            <p className="text-[11px] mt-1 mb-5" style={{ color: MUTED, lineHeight: 1.55 }}>
              Take one 60-second AI body scan. All future orders will use these measurements automatically —
              no re-measuring needed.
            </p>
            <StartBodyScanButton onComplete={handleScanComplete} testId="start-scan-empty-state" />
          </div>
        )}

        {!loading && !error && profile && (
          <>
            {/* Active profile summary */}
            <div className="p-4 mb-4"
              style={{
                background: 'linear-gradient(135deg, rgba(212,175,55,0.06), rgba(229,178,185,0.06))',
                border: `1px solid rgba(212,175,55,0.45)`,
              }}
              data-testid="active-profile-card">
              {/* AI scan status banner — only for authoritative scan saves */}
              {profile.source === '3dlook_api' && (
                <div
                  data-testid="ai-scan-status-banner"
                  className="mb-3 px-3 py-2 flex items-center gap-2"
                  style={{
                    background: 'rgba(22,101,52,0.08)',
                    border: '1px solid rgba(22,101,52,0.35)',
                  }}>
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#166534' }} />
                  <div className="text-[9.5px]" style={{ color: '#166534', lineHeight: 1.5, letterSpacing: '0.06em' }}>
                    <b>AI Body Scan: Completed</b><br />
                    <span style={{ color: MUTED }}>Measurement Date: {fmtDate(profile.scan_date)}</span>
                  </div>
                </div>
              )}
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Sparkles className="w-3.5 h-3.5" style={{ color: GOLD }} strokeWidth={1.6} />
                    <p className="text-[9px] tracking-[0.22em] uppercase" style={{ color: GOLD, fontWeight: 700 }}>
                      Active Fit Profile
                    </p>
                  </div>
                  <p className="text-[11px]" style={{ color: INK, lineHeight: 1.5 }}>
                    Captured {fmtDate(profile.scan_date)}
                    {age != null && <> · {age} {age === 1 ? 'day' : 'days'} old</>}
                    {profile.confidence?.overall && <> · {profile.confidence.overall}% AI</>}
                  </p>
                  <p className="text-[9.5px] mt-0.5" style={{ color: MUTED }}>
                    Provider: <span style={{ color: INK, fontWeight: 500 }}>{profile.provider || 'lunora'}</span>
                    {profile.saia_person_id && <> · scan {profile.saia_person_id.slice(0, 8)}…</>}
                  </p>
                </div>
                {profile.avatar_url && (
                  <a href={profile.avatar_url} target="_blank" rel="noreferrer"
                    className="flex-shrink-0 flex items-center gap-1 text-[9.5px] tracking-[0.14em] uppercase"
                    style={{ color: GOLD, fontWeight: 600 }}>
                    3D Avatar <ExternalLink className="w-2.5 h-2.5" strokeWidth={1.8} />
                  </a>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div>
                  <p className="text-[9px] tracking-[0.10em] uppercase" style={{ color: MUTED }}>Height</p>
                  <p className="text-[13px]" style={{ color: INK, fontWeight: 500 }}>{profile.height_cm} cm</p>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.10em] uppercase" style={{ color: MUTED }}>Weight</p>
                  <p className="text-[13px]" style={{ color: INK, fontWeight: 500 }}>{profile.weight_kg} kg</p>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.10em] uppercase" style={{ color: MUTED }}>Gender</p>
                  <p className="text-[13px] capitalize" style={{ color: INK, fontWeight: 500 }}>{profile.gender}</p>
                </div>
              </div>
            </div>

            {/* Primary measurements */}
            <div className="mb-4">
              <div className="flex items-center gap-1.5 mb-2">
                <Ruler className="w-3 h-3" style={{ color: GOLD }} strokeWidth={1.6} />
                <p className="text-[9px] tracking-[0.18em] uppercase" style={{ color: MUTED, fontWeight: 700 }}>
                  Primary Measurements
                </p>
              </div>
              {hasProvider ? (
                <div className="grid grid-cols-2 gap-2" data-testid="primary-measurements">
                  {primaryProvider.map(({ key, item }) => (
                    <div key={key} data-testid={`primary-${key}`}>
                      <MeasurementCard label={item ? item.label : humanize(key.split('.').pop())}
                        value={item ? item.value : 'Unavailable'} unit={item ? item.unit : ''} />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <p data-testid="summary-values-note" className="text-[10px] mb-2" style={{ color: MUTED }}>
                    Full 3DLOOK measurement list is not stored for this profile — showing summary values (rounded to 0.1 cm).
                  </p>
                  <div className="grid grid-cols-2 gap-2" data-testid="primary-measurements">
                    {primary.map((k) => (
                      <MeasurementCard key={k} label={humanize(k)} value={measurements[k]} />
                    ))}
                  </div>
                </>
              )}
            </div>

            {hasProvider && (
              <div className="mb-4 bg-white" data-testid="all-provider-measurements" style={{ border: `1px solid ${LINE}` }}>
                <button onClick={() => setShowHistory((v) => !v)} data-testid="advanced-toggle"
                  className="w-full px-3 py-2.5 flex items-center justify-between">
                  <p className="text-[9px] tracking-[0.18em] uppercase" style={{ color: MUTED, fontWeight: 700 }}>
                    All 3DLOOK Body Measurements · {provider.count}
                  </p>
                  {showHistory ? <ChevronUp className="w-3.5 h-3.5" style={{ color: MUTED }} strokeWidth={1.5} />
                               : <ChevronDown className="w-3.5 h-3.5" style={{ color: MUTED }} strokeWidth={1.5} />}
                </button>
                {showHistory && <ProviderMeasurements data={provider} />}
              </div>
            )}

            {/* Advanced measurements (legacy summary only) */}
            {!hasProvider && rest.length > 0 && (
              <div className="mb-4" data-testid="advanced-measurements-section">
                <button
                  onClick={() => setShowHistory((v) => !v)}
                  data-testid="advanced-toggle"
                  className="w-full px-3 py-2.5 flex items-center justify-between"
                  style={{ background: '#FFF', border: `1px solid ${LINE}` }}>
                  <div className="text-left">
                    <p className="text-[9px] tracking-[0.18em] uppercase" style={{ color: MUTED, fontWeight: 700 }}>
                      Detailed Measurements
                    </p>
                    <p className="text-[10px]" style={{ color: MUTED }}>{rest.length} additional fields</p>
                  </div>
                  {showHistory ? <ChevronUp className="w-3.5 h-3.5" style={{ color: MUTED }} strokeWidth={1.5} />
                               : <ChevronDown className="w-3.5 h-3.5" style={{ color: MUTED }} strokeWidth={1.5} />}
                </button>
                {showHistory && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {rest.map((k) => (
                      <MeasurementCard key={k} label={humanize(k)} value={measurements[k]} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Rescan CTA */}
            <div className="mb-4 p-4" style={{ background: '#FFF', border: `1px solid ${LINE}` }}>
              <div className="flex items-start gap-2 mb-3">
                <RefreshCw className="w-3.5 h-3.5 mt-0.5" style={{ color: GOLD }} strokeWidth={1.6} />
                <div>
                  <p className="text-[12px]" style={{ color: INK, fontWeight: 500 }}>Body changed?</p>
                  <p className="text-[10.5px] mt-0.5" style={{ color: MUTED, lineHeight: 1.5 }}>
                    Take a fresh scan. Your old scans stay in history — nothing gets lost.
                  </p>
                </div>
              </div>
              <StartBodyScanButton onComplete={handleScanComplete} testId="start-scan-rescan" secondary />
            </div>

            {/* Scan history */}
            {history.length > 1 && (
              <div className="mb-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <History className="w-3 h-3" style={{ color: GOLD }} strokeWidth={1.6} />
                  <p className="text-[9px] tracking-[0.18em] uppercase" style={{ color: MUTED, fontWeight: 700 }}>
                    Scan History ({history.length})
                  </p>
                </div>
                <div className="space-y-2" data-testid="history-list">
                  {history.map((scan) => (
                    <HistoryRow
                      key={scan.profile_id}
                      scan={scan}
                      isActive={scan.is_active}
                      onActivate={handleActivate}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {profile && (
          <p className="text-[9.5px] text-center flex items-center justify-center gap-1 py-3" style={{ color: MUTED }}>
            <ShieldCheck className="w-2.5 h-2.5" style={{ color: GREEN }} strokeWidth={1.6} />
            All scans encrypted at rest · Only you and your assigned tailor can view them
          </p>
        )}
      </div>
    </div>
  );
};

export default MyFitProfile;
